import { Injectable } from '@angular/core';
import { Observable, combineLatest, map, of, switchMap } from 'rxjs';
import { DevicesStore } from './devices.store';
import { InstallationFeaturesStore } from '../installation-features';
import {
  DeviceBus,
  DeviceNameCommands,
  DeviceNameProperties,
  FeatureNames,
} from '@vi-iot/client/index';
import { RichDevice } from './devices.state';
import { TranslateService } from '@ngx-translate/core';
import { DevicesServiceModels } from './devices.service.models';

@Injectable({
  providedIn: 'root',
})
export class DevicesService {
  wallboxDevicesWithBusAddress$: Observable<
    DevicesServiceModels.WallboxDeviceWithBusAddres[]
  > = this.devicesStore.getAllWallboxDevicesWithMatchingBoilerSerial$.pipe(
    switchMap((devices) => {
      const deviceBusFeatures = devices.map((device) =>
        this.installationFeaturesStore
          .selectInstallationDeviceFeature<DeviceBus, never>(FeatureNames.deviceBus, device.id)
          .pipe(
            map((feature) => ({
              busAddress: feature?.properties?.busAddress?.value,
              device,
            }))
          )
      );

      return combineLatest(deviceBusFeatures);
    }),
    map((data) => data.sort((a, b) => Number(a.busAddress || 0) - Number(b.busAddress || 0)))
  );

  wallboxDevicesWithUInames$: Observable<
    DevicesServiceModels.WallboxDeviceWithUIName[]
  > = this.devicesStore.getAllWallboxDevicesWithMatchingBoilerSerial$.pipe(
    switchMap((devices) => {
      if (!devices || devices.length === 0) {
        return of([]);
      }
      const mappedDevices = devices
        // sort by index in name 'eebus-{index}'
        .sort((a, b) => Number(a.id.split('-')[1] || 0) - Number(b.id.split('-')[1] || 0))
        .map((device) =>
          this.getOBNGWallboxDeviceName(device).pipe(
            map((name) => ({
              device,
              name,
            }))
          )
        );

      return combineLatest(mappedDevices);
    })
  );

  constructor(
    private devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private translate: TranslateService
  ) {}

  getOBNGWallboxDeviceNameByBusAddress(busAddress: string): Observable<string> {
    return this.wallboxDevicesWithBusAddress$.pipe(
      switchMap((devices) => {
        const matchingDevice = devices.find((device) => device.busAddress === busAddress);

        return this.getOBNGWallboxDeviceName(matchingDevice.device);
      })
    );
  }

  getDryContactGenericDeviceName(device: RichDevice): Observable<string> {
    const deviceNumber = device.id.split('-').pop() ?? '';
    const fallbackName = `${this.translate.instant(
      '+INSTALLATIONS.DRY_CONTACTS.GENERIC_CONSUMER.OTHER_DEVICE'
    )} ${deviceNumber}`;

    return this.installationFeaturesStore
      .selectInstallationDeviceFeature<DeviceNameProperties, DeviceNameCommands>(
        FeatureNames.deviceName,
        device.id
      )
      .pipe(map((feature) => feature?.properties?.name?.value?.trim() || fallbackName));
  }

  getOBNGWallboxDeviceName(device: RichDevice): Observable<string> {
    const deviceName$: Observable<string> = this.installationFeaturesStore
      .selectInstallationDeviceFeature<DeviceNameProperties, DeviceNameCommands>(
        FeatureNames.deviceName,
        device.id
      )
      .pipe(map((feature) => feature?.properties?.name?.value));

    const alternativeName$: Observable<string> = this.installationFeaturesStore
      .selectInstallationDeviceFeature<DeviceBus, never>(FeatureNames.deviceBus, device.id)
      .pipe(
        map((feature) => {
          const busAddress = feature?.properties?.busAddress?.value ?? '';

          return (
            this.translate.instant(
              '+INSTALLATIONS.INSTALLATION.WALLBOX_TILE_NAME_CHARGING_STATION'
            ) + ` ${busAddress}`
          );
        })
      );

    return combineLatest([deviceName$, alternativeName$]).pipe(
      map(([deviceName, alternativeName]) => deviceName?.trim() || alternativeName)
    );
  }
}
